/**
 * DemoSandboxBadge — Iter-94 · PM Demo Watermark.
 *
 * Fixed top-right badge visible ONLY while the current session is a demo
 * sandbox. Polls /api/commercial/demo-session every 30 seconds for the
 * server-authoritative remaining time and re-renders a live countdown
 * every second in between.
 *
 * Color transitions
 *   • green  — more than 6 hours remaining
 *   • amber  — under 6 hours
 *   • red    — under 1 hour
 *
 * On expiry the client wipes the session token and redirects the prospect
 * to /demo-ended.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Timer } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DemoSandboxBadge() {
  const [state, setState] = useState({ is_demo: false, remaining: 0, expired: false });
  const navigate = useNavigate();
  const redirectedRef = useRef(false);

  // Server poll — every 30s to correct drift.
  const fetchStatus = async () => {
    try {
      const r = await fetch(`${API}/api/commercial/demo-session`, { credentials: 'include' });
      if (!r.ok) return;
      const d = await r.json();
      if (!d.is_demo) {
        setState({ is_demo: false, remaining: 0, expired: false });
        return;
      }
      setState({
        is_demo: true,
        remaining: Number(d.remaining_seconds || 0),
        expired: Boolean(d.expired),
      });
    } catch { /* silent */ }
  };

  useEffect(() => {
    fetchStatus();
    const server = setInterval(fetchStatus, 30_000);
    // Local tick — decrement remaining every second between polls.
    const local = setInterval(() => {
      setState((s) => s.is_demo && !s.expired
        ? { ...s, remaining: Math.max(0, s.remaining - 1), expired: s.remaining <= 1 }
        : s);
    }, 1_000);
    return () => { clearInterval(server); clearInterval(local); };
  }, []);

  useEffect(() => {
    if (state.is_demo && state.expired && !redirectedRef.current) {
      redirectedRef.current = true;
      try {
        localStorage.removeItem('session_token');
        document.cookie = 'session_token=; path=/; max-age=0';
      } catch { /* ignore */ }
      navigate('/demo-ended', { replace: true });
    }
  }, [state.is_demo, state.expired, navigate]);

  if (!state.is_demo || state.expired) return null;

  const hours = Math.floor(state.remaining / 3600);
  const minutes = Math.floor((state.remaining % 3600) / 60);
  const label = `${hours}h ${minutes.toString().padStart(2, '0')}m`;

  // Color tier per spec.
  const tier =
    state.remaining < 3_600 ? 'red'
    : state.remaining < 21_600 ? 'amber'
    : 'green';
  const palette = {
    green: { bg: 'rgba(16,185,129,0.15)', border: '#059669', text: '#a7f3d0', dot: '#10b981' },
    amber: { bg: 'rgba(245,158,11,0.15)', border: '#d97706', text: '#fde68a', dot: '#f59e0b' },
    red:   { bg: 'rgba(239,68,68,0.18)',  border: '#dc2626', text: '#fecaca', dot: '#ef4444' },
  }[tier];

  return (
    <div
      className="fixed top-4 right-4 z-[9999] flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-lg backdrop-blur-sm select-none"
      style={{
        backgroundColor: palette.bg,
        borderColor: palette.border,
        color: palette.text,
        fontFamily: 'Barlow Condensed, sans-serif',
      }}
      data-testid="demo-sandbox-badge"
    >
      <span
        className="w-2 h-2 rounded-full animate-pulse"
        style={{ backgroundColor: palette.dot }}
        data-testid="demo-sandbox-badge-dot"
      />
      <Timer className="h-3.5 w-3.5" />
      <span className="text-[11px] font-black uppercase tracking-widest" data-testid="demo-sandbox-badge-label">
        Demo Sandbox
      </span>
      <span className="text-[11px] font-mono opacity-90" data-testid="demo-sandbox-badge-countdown">
        expires in {label}
      </span>
    </div>
  );
}
